import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Autocomplete,
  Box,
  Button,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Navbar } from '../../component/shared/Navbar'
import { PageContainer } from '../../component/styled/PageContainer'
import { CsvPreviewSection } from './components/CsvPreviewSection'
import { MessageTemplatePreview } from './components/MessageTemplatePreview'
import { useGetWhatsappConfigs } from '../../api/whatsapp-config'
import {
  useGetWhatsappTemplates,
  useUploadWhatsappMedia,
  useSendWhatsappBroadcast,
} from '../../api/whatsapp-broadcast'
import { useSnackbar } from 'notistack'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { useNavigate } from 'react-router-dom'

export const WhatsappBroadcastScreen = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [selectedPhoneOption, setSelectedPhoneOption] = useState(null)
  const [selectedTemplateOption, setSelectedTemplateOption] = useState(null)
  const [headerMediaId, setHeaderMediaId] = useState('')
  const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState('')
  const [rows, setRows] = useState([])

  const selectedPhone = selectedPhoneOption?.phoneNumber || ''

  const { data: configData = [] } = useGetWhatsappConfigs()
  const { data: templates = [], isLoading: isLoadingTemplates } = useGetWhatsappTemplates(
    { phoneNumber: selectedPhone },
    { enabled: !!selectedPhone },
  )
  const { mutateAsync: uploadMedia, isLoading: isUploadingMedia } = useUploadWhatsappMedia()
  const { mutateAsync: sendBroadcast, isLoading: isSending } = useSendWhatsappBroadcast()

  const onChangePhone = (option) => {
    setSelectedPhoneOption(option)
    setSelectedTemplateOption(null)
    setRows([])
    setHeaderMediaId('')
    setHeaderImagePreviewUrl('')
  }

  const onChangeTemplate = (option) => {
    setSelectedTemplateOption(option)
    setRows([])
    setHeaderMediaId('')
    setHeaderImagePreviewUrl('')
  }

  const validateForm = () => {
    if (!selectedPhoneOption) return 'Sender phone number is required.'

    if (!selectedTemplateOption) return 'Template is required.'

    if (!rows || rows.length === 0) return 'CSV data is required. Please upload CSV file.'

    if (selectedTemplateOption?.hasImageHeader && !headerMediaId)
      return 'Header image is required for the selected template.'

    return null
  }

  const onSend = async () => {
    const err = validateForm()

    if (err) return enqueueSnackbar(err, { variant: 'error' })

    const payload = {
      phoneNumber: selectedPhone,
      templateName: selectedTemplateOption.name,
      headerMediaId: headerMediaId || undefined,
      rows,
    }

    try {
      const { data } = await sendBroadcast(payload)

      enqueueSnackbar('Broadcast started successfully. Redirecting...', { variant: 'success' })

      navigate(`/whatsapp-broadcast-progress/${data.jobId}`)
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' })
    }
  }

  const onUploadHeaderImage = async (event) => {
    const file = event?.target?.files?.[0]

    if (!file)
      return enqueueSnackbar('Please select an image.', {
        variant: 'error',
      })

    if (!selectedPhone)
      return enqueueSnackbar('Please select sender phone first.', { variant: 'error' })

    const previewUrl = URL.createObjectURL(file)

    setHeaderImagePreviewUrl(previewUrl)
    setHeaderMediaId('')

    const formData = new FormData()
    formData.append('phoneNumber', selectedPhone)
    formData.append('file', file)

    try {
      const { data } = await uploadMedia(formData)
      setHeaderMediaId(data?.id || '')
      enqueueSnackbar('Header image uploaded successfully.', { variant: 'success' })
    } catch (error) {
      setHeaderImagePreviewUrl('')
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' })
    }
  }

  return (
    <>
      <Helmet titleTemplate='%s | Staff Portal' defaultTitle='Staff Portal'>
        <title>Whatsapp Bulk Broadcast</title>
      </Helmet>
      <Navbar />
      <PageContainer>
        <Box my={3}>
          <Typography variant='h1'>Whatsapp Bulk Broadcast</Typography>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={configData}
                value={selectedPhoneOption}
                disableClearable
                onChange={(_, option) => onChangePhone(option)}
                getOptionLabel={(option) => `${option.displayName} (${option.phoneNumber})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Sender Phone Number'
                    placeholder='Select phone number'
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={templates}
                value={selectedTemplateOption}
                disableClearable
                disabled={!selectedPhone || isLoadingTemplates}
                onChange={(_, option) => onChangeTemplate(option)}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label='Template' placeholder='Select template' />
                )}
              />
            </Grid>
            <CsvPreviewSection
              selectedTemplateOption={selectedTemplateOption}
              rows={rows}
              setRows={setRows}
            />
            {selectedTemplateOption?.hasImageHeader && (
              <Grid item xs={12}>
                <Button variant='outlined' component='label' disabled={isUploadingMedia}>
                  Upload Header Image
                  <input hidden type='file' accept='image/*' onChange={onUploadHeaderImage} />
                </Button>
                <FormHelperText>
                  {headerMediaId
                    ? `Uploaded media ID: ${headerMediaId}`
                    : 'Header image is required.'}
                </FormHelperText>
              </Grid>
            )}
            <Grid container justifyContent='center' marginTop={2}>
              <Grid item xs={12} md='auto'>
                <MessageTemplatePreview
                  rows={rows}
                  selectedTemplate={selectedTemplateOption}
                  headerImagePreviewUrl={headerImagePreviewUrl}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box display='flex' justifyContent='flex-end'>
                <Button variant='contained' onClick={onSend} disabled={isSending}>
                  Send Broadcast
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </PageContainer>
    </>
  )
}
